import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Grid,
  Paper,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const Imex = () => {
  const [selectedTable, setSelectedTable] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [selectedHeaders, setSelectedHeaders] = useState([]);
  const [fileData, setFileData] = useState([]); // full file data
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState("");

  const tables = ["users", "products", "orders", "customers", "inventory"];

  // --- Handle file upload ---
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".csv")) {
      alert("Please upload a valid Excel (.xlsx) or CSV (.csv) file.");
      return;
    }

    setUploadedFile(file);

    if (fileName.endsWith(".csv")) {
      Papa.parse(file, {
        header: true,
        complete: (result) => {
          const cols = result.meta.fields || [];
          setHeaders(cols.sort((a, b) => a.localeCompare(b)));
          setSelectedHeaders([]);
          setFileData(result.data);
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (sheetData.length > 1) {
          const cols = sheetData[0];
          const rows = XLSX.utils.sheet_to_json(sheet);
          setHeaders(cols.sort((a, b) => a.localeCompare(b)));
          setSelectedHeaders([]);
          setFileData(rows);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleTableChange = (event) => setSelectedTable(event.target.value);

  // --- Move header from left → right ---
  const handleMoveRight = (header) => {
    setSelectedHeaders((prev) =>
      [...prev, header].sort((a, b) => a.localeCompare(b))
    );
    setHeaders((prev) =>
      prev.filter((h) => h !== header).sort((a, b) => a.localeCompare(b))
    );
  };

  // --- Move header from right → left ---
  const handleMoveLeft = (header) => {
    setHeaders((prev) =>
      [...prev, header].sort((a, b) => a.localeCompare(b))
    );
    setSelectedHeaders((prev) =>
      prev.filter((h) => h !== header).sort((a, b) => a.localeCompare(b))
    );
  };

  // --- Filtered data ---
  const getFilteredData = () =>
    fileData.map((row) => {
      const filteredRow = {};
      selectedHeaders.forEach((header) => {
        filteredRow[header] = row[header];
      });
      return filteredRow;
    });

  // --- Handle Import to DB ---
  const handleImport = async () => {
    if (!uploadedFile) {
      alert("Please upload a file first.");
      return;
    }
    if (!selectedTable) {
      alert("Please select a target database table.");
      return;
    }
    if (selectedHeaders.length === 0) {
      alert("Please select at least one column to import.");
      return;
    }

    const filteredData = getFilteredData();

    setLoading(true);
    try {
      const response = await fetch("/api/import", {
        // replace with your actual backend API
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tableName: selectedTable,
          data: filteredData,
        }),
      });

      if (!response.ok) throw new Error("Failed to import data");
      const result = await response.json();
      alert(`✅ Data imported successfully: ${result.message || "Done"}`);
    } catch (error) {
      console.error("Import failed:", error);
      alert("❌ Import failed. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  // --- Handle Export (download filtered file) ---
  const handleExport = () => {
    if (!uploadedFile) {
      alert("Please upload a file first.");
      return;
    }
    if (selectedHeaders.length === 0) {
      alert("Please select at least one column to export.");
      return;
    }

    const filteredData = getFilteredData();

    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered Data");

    const exportFileName = uploadedFile.name.replace(/\.(csv|xlsx)$/i, "") + "_filtered";

    // Export as Excel
    XLSX.writeFile(workbook, `${exportFileName}.xlsx`);

    // Optional: also export CSV
    // const csv = Papa.unparse(filteredData);
    // const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    // const link = document.createElement("a");
    // link.href = URL.createObjectURL(blob);
    // link.download = `${exportFileName}.csv`;
    // link.click();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      minHeight="90vh"
      textAlign="center"
      sx={{ p: 4 }}
    >
      <Typography variant="h3" gutterBottom>
        Imex
      </Typography>

      <Typography variant="body1" color="text.secondary" mb={4}>
        Upload Excel/CSV data, select columns, import or export filtered data.
      </Typography>

      {/* Upload + Dropdown */}
      <Stack direction="row" spacing={2} alignItems="center" mb={4}>
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadFileIcon />}
        >
          {uploadedFile ? uploadedFile.name : "Upload File"}
          <input
            type="file"
            accept=".xlsx,.csv"
            hidden
            onChange={handleFileChange}
          />
        </Button>

        
    <FormControl sx={{ minWidth: 150 }}>
      <InputLabel>File Type</InputLabel>
      <Select
        value={exportType}
        onChange={(e) => setExportType(e.target.value)}
        label="File type"
      >
        <MenuItem value="excel">Excel</MenuItem>
        <MenuItem value="pdf">PDF</MenuItem>
        <MenuItem value="csv">CSV</MenuItem>
      </Select>
    </FormControl>
 

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Select Adaptor</InputLabel>
          <Select
            value={selectedTable}
            onChange={handleTableChange}
            label="Select Adaptor"
          >
            {tables.map((table, index) => (
              <MenuItem key={index} value={table}>
                {table}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Grids */}
      {headers.length > 0 || selectedHeaders.length > 0 ? (
        <>
          <Grid container spacing={4} justifyContent="center">
            {/* Left Grid */}
            <Grid item xs={12} md={5}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Available Columns
                </Typography>
                {headers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No columns available
                  </Typography>
                ) : (
                  <List dense>
                    {headers.map((header, index) => (
                      <ListItem
                        key={index}
                        onClick={() => handleMoveRight(header)}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            cursor: "pointer",
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox checked={false} />
                        </ListItemIcon>
                        <ListItemText primary={header} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>

            {/* Right Grid */}
            <Grid item xs={12} md={5}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Selected Columns
                </Typography>
                {selectedHeaders.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No columns selected
                  </Typography>
                ) : (
                  <List dense>
                    {selectedHeaders.map((header, index) => (
                      <ListItem
                        key={index}
                        onClick={() => handleMoveLeft(header)}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            cursor: "pointer",
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox checked />
                        </ListItemIcon>
                        <ListItemText primary={header} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Import + Export Buttons */}
          <Stack direction="row" spacing={3} mt={4}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CloudUploadIcon />}
              onClick={handleImport}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Import Data"}
            </Button>

                      <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<CloudDownloadIcon />}
                          onClick={handleExport}
                          disabled={selectedHeaders.length === 0}
                      >
                          Export Filtered Data
                      </Button>
          </Stack>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Please upload a file to view column headers.
        </Typography>
      )}
    </Box>
  );
};

export default Imex;
